import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Image as ImageIcon } from "lucide-react";
import { api, resolveAssetUrl } from "@/lib/api";
import { toast } from "sonner";
import ImageUploader from "@/components/admin/ImageUploader";
import RepeaterList from "@/components/admin/RepeaterList";

const empty = { title: "", description: "", cover_image: "", order: 100, photos: [] };

function F({ label, children }) {
  return (
    <div className="space-y-1.5">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-ink-muted">{label}</div>
      {children}
    </div>
  );
}

export default function AdminGallery() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/admin/galleries"); setItems(r.data || []); }
    catch (e) { toast.error(`Failed to load: ${e.message}`); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ ...empty, order: (items.length + 1) * 10 }); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...empty, ...it, photos: it.photos || [] }); setOpen(true); };

  const save = async () => {
    if (!form.title?.trim()) return toast.error("Album title is required");
    try {
      if (editing) await api.patch(`/admin/galleries/${editing.id}`, form);
      else await api.post("/admin/galleries", form);
      toast.success(editing ? "Album updated" : "Album created");
      setOpen(false); await load();
    } catch (e) { toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this album and all its photos?")) return;
    try { await api.delete(`/admin/galleries/${id}`); toast.success("Album deleted"); await load(); }
    catch (e) { toast.error(`Delete failed: ${e.message}`); }
  };

  return (
    <div className="space-y-6" data-testid="admin-gallery-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="overline">Gallery</div>
          <h1 className="font-display text-3xl font-extrabold mt-2 text-navy">Photo Albums</h1>
          <p className="text-sm text-ink-muted mt-1.5">Create albums, upload photos. Visitors see all albums under /gallery with a full-screen lightbox viewer.</p>
        </div>
        <Button onClick={openNew} className="btn-saffron rounded-full" data-testid="add-album-btn">
          <Plus className="h-4 w-4 mr-2"/> New album
        </Button>
      </div>

      {loading ? <div className="text-ink-muted">Loading…</div> :
        items.length === 0 ? (
          <div className="card-flat p-12 text-center" data-testid="gallery-empty">
            <ImageIcon className="h-10 w-10 mx-auto text-saffron"/>
            <div className="font-display font-bold text-navy mt-4">No albums yet</div>
            <div className="text-sm text-ink-muted mt-1">Click "New album" above to create your first one.</div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map(a => (
              <div key={a.id} className="card-flat overflow-hidden" data-testid={`album-card-${a.id}`}>
                <div className="aspect-[4/3] bg-slate-100 relative overflow-hidden">
                  {a.cover_image ? (
                    <img src={resolveAssetUrl(a.cover_image)} alt={a.title} className="h-full w-full object-cover"/>
                  ) : a.photos?.[0]?.image ? (
                    <img src={resolveAssetUrl(a.photos[0].image)} alt={a.title} className="h-full w-full object-cover"/>
                  ) : (
                    <div className="h-full w-full grid place-items-center text-ink-muted text-sm">No cover</div>
                  )}
                  <div className="absolute top-3 left-3 text-[10px] uppercase tracking-wider font-bold bg-white/90 text-navy px-2 py-1 rounded-full">
                    {(a.photos || []).length} photo{(a.photos || []).length !== 1 ? "s" : ""}
                  </div>
                </div>
                <div className="p-5">
                  <div className="font-display font-bold text-navy">{a.title}</div>
                  {a.description && <div className="text-xs text-ink-muted mt-1 line-clamp-2">{a.description}</div>}
                  <div className="flex items-center gap-2 mt-4">
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => openEdit(a)} data-testid={`edit-album-${a.id}`}>
                      <Pencil className="h-3.5 w-3.5 mr-1.5"/> Edit
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-full text-red-600 border-red-200 hover:bg-red-50" onClick={() => remove(a.id)} data-testid={`delete-album-${a.id}`}>
                      <Trash2 className="h-3.5 w-3.5 mr-1.5"/> Delete
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      }

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit album" : "New album"}</DialogTitle></DialogHeader>
          <div className="space-y-5 mt-2">
            <div className="grid sm:grid-cols-2 gap-4">
              <F label="Album title"><Input data-testid="album-title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}/></F>
              <F label="Order (lower number = shown first)"><Input data-testid="album-order" type="number" value={form.order} onChange={e => setForm({ ...form, order: parseInt(e.target.value, 10) || 0 })}/></F>
            </div>
            <F label="Description (optional)"><Textarea data-testid="album-desc" rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}/></F>
            <ImageUploader testid="album-cover" label="Cover image (optional — first photo will be used if empty)" value={form.cover_image} onChange={v => setForm({ ...form, cover_image: v })}/>

            <div>
              <div className="font-display font-bold text-navy text-sm uppercase tracking-wider mb-3">Photos in this album</div>
              <RepeaterList testid="album-photos" addLabel="Add photo"
                items={form.photos || []} onChange={(arr) => setForm({ ...form, photos: arr })}
                makeNew={() => ({ image: "", caption: "" })}
                render={(it, idx, setI) => (
                  <div className="grid sm:grid-cols-12 gap-3 items-start">
                    <div className="sm:col-span-7"><ImageUploader testid={`album-photo-img-${idx}`} label={`Photo #${idx+1}`} value={it.image} onChange={v => setI({ image: v })}/></div>
                    <div className="sm:col-span-5"><F label="Caption (optional)"><Input data-testid={`album-photo-caption-${idx}`} value={it.caption || ""} onChange={e => setI({ caption: e.target.value })}/></F></div>
                  </div>
                )}
              />
            </div>
          </div>
          <div className="flex gap-2 mt-6">
            <Button onClick={save} className="btn-saffron rounded-full" data-testid="save-album-btn">{editing ? "Save changes" : "Create album"}</Button>
            <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
